'use client';
//外部依赖
import { useInViewport } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { getSnapshot, Instance } from 'mobx-state-tree';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
//内部组件
import ProductList from '@/components/productList';
import UKPageError from '@/components/UKPageError';
import UKPageLoading from '@/components/UKPageLoading/index';
//store
import { useStores } from '@/models';
//埋点
import { sendClickTrack as HOME_CHANNEL_PAGE_sendClickTrack } from '@/tracks/32401';
//接口和ts
import {
  queryProductRecommendData,
  queryRecommendationGetSlotList,
} from '@/apis/recommend/recommend';
import { ChannelPageType, ISlotItem, SceneCode } from '@/apis/recommend/recommend.interface';
import type { IRecommendStore, RecommendStoreModel } from '@/models/RecommendStore';
//埋点
import { sendClickTrack as HOME_PAGE_sendClickTrack } from '@/tracks/32631';
//多语言
import { useTranslation } from '@/i18n/client';
//样式
import style from './style.module.scss';
export interface trackParamsType {
  RecomSku_Expo?: string;
  RecomSku_Addcart_Expo?: string;
  productClick?: (json_param: any) => void;
  addCartClick?: (json_param: any) => void;
  extraParams?: Record<string, any>;
}

interface productTrackParamsType {
  RecomSku_Expo?: string;
  RecomSku_Addcart_Expo?: string;
  productClick?: (json_param: any) => void;
  addCartClick?: (json_param: any) => void;
  tab_name?: string;
  extraParams?: Record<string, any>;
}
interface RecommendProps {
  initRecommendStore?: IRecommendStore;
  sceneCode?: SceneCode;
  subSceneCode?: string;
  mainSkus?: string;
  bulkPrice?: string;
  c3Id?: string;
  trackParams?: trackParamsType;
  channelPage?: ChannelPageType;
  addCartFn?: (item: any) => void;
  shouldShowModal?: boolean;
  shouldShowService?: boolean;
  onAddCartSuccess?: (item: any) => void;
  //最小展示数量
  minShowCount?: number;
  isSearch?: boolean;
}

type RecommendTab = Instance<typeof RecommendStoreModel>['recommendData'][number];

const Recommend = observer(
  ({
    initRecommendStore,
    sceneCode = SceneCode.HOME_PAGE,
    subSceneCode,
    mainSkus,
    c3Id,
    bulkPrice,
    trackParams,
    channelPage,
    shouldShowModal = false,
    shouldShowService = false,
    addCartFn,
    onAddCartSuccess,
    minShowCount,
    isSearch = false,
  }: RecommendProps) => {
    const { t } = useTranslation();
    const pathname = usePathname(); // 使用 Next.js 的 hook
    //根据传入initRecommendStore进行store的初始化操作，ssr中需要使用useStoreSsr
    const { recommendStore } = useStores(); //推荐组件store
    const loadMoreRef = useRef<HTMLDivElement>(null); //加载更多哨兵

    const [isVisible, ratio] = useInViewport(loadMoreRef, {
      //这里必须要填loadMoreRef，不能填loadMoreRef.current否则可能会获取不到元素
      rootMargin: '0px 0px 1000px 0px',
      threshold: 0,
    });

    //先初始化曝光埋点
    const productTrackParams = useRef<productTrackParamsType>({
      RecomSku_Expo: trackParams?.RecomSku_Expo,
      RecomSku_Addcart_Expo: trackParams?.RecomSku_Addcart_Expo,
      productClick: trackParams?.productClick,
      addCartClick: trackParams?.addCartClick,
      extraParams: trackParams?.extraParams,
    });
    const [isShowViewMore, setIsShowViewMore] = useState(false); //是否展示view more按钮
    const loading = useRef(false);
    const maxProducts = 40; // 展示x商品数量后显示view more

    //点击tab方法
    const handleTabClick = (tab: RecommendTab) => {
      const tabIndex = recommendStore?.recommendData.findIndex(
        (item) => item.slotId === tab.slotId,
      );
      if (tabIndex !== -1 && tabIndex !== recommendStore.activeTab) {
        recommendStore.setProp('activeTab', tabIndex);
        //切换tab时只有第一次加载数据时才需要获取数据
        if (recommendStore.recommendData[tabIndex].productList.length === 0) {
          recommendStore.recommendData[tabIndex].loadProductData();
        }
      }
    };

    //点击View mores事件
    const handleMore = () => {
      setIsShowViewMore(false);
      recommendStore.currentRecommendData?.loadProductData();
    };

    //判断是否展示View more
    const changeViewMore = () => {
      if (
        recommendStore.currentRecommendData.noMores ||
        recommendStore.currentRecommendData.errors
      ) {
        setIsShowViewMore(false);
        return false;
      }
      const length = recommendStore.currentRecommendData?.productList?.length;
      // 如果当前tab的数据大于2屏，则展示view more
      if (length / maxProducts >= recommendStore.currentRecommendData.nowPage) {
        // console.log('sxt-超过40条数据了');
        setIsShowViewMore(true);
        recommendStore.currentRecommendData.setProp(
          'nowPage',
          recommendStore.currentRecommendData.nowPage + 1,
        );
        return true;
      }
      return false;
    };

    const initRecommend = async () => {
      if (loading.current) {
        return;
      }
      loading.current = true;
      defaultRecommendData();

      // 获取推荐tab列表
      const resSlotList = await queryRecommendationGetSlotList({ sceneCode });
      //判断如果没有获取到则直接返回
      if (resSlotList.code !== '200') return (loading.current = false);
      if (!resSlotList.data?.slotScopeVOS?.length) return (loading.current = false);

      const slotList = resSlotList.data.slotScopeVOS;

      //根据slotList的长度来生成新的数据
      const recommendData = slotList.map((item: ISlotItem, index: number) => ({
        slotName: item.slotName,
        slotId: item.slotId,
        page: index === 0 ? 2 : 1,
        pageSize: 20,
        nowPage: 1,
        productList: [],
        localInfo: {},
        userInfoVO: {},
        loading: false,
        noMores: false,
        errors: false,
      }));

      //根据第一个推荐tab为参数获取推荐数据
      const resRecommend = await queryProductRecommendData({
        sceneCode,
        subSceneCode,
        mainSkus,
        c3Id,
        channelPage,
        bulkPrice: bulkPrice ? bulkPrice : slotList[0].slotId,
        page: 1,
        pageSize: 20,
      });
      const { data } = resRecommend;
      //判断如果没有获取到则直接返回
      if (!data || !data.paragraphs || data?.paragraphs?.length === 0)
        return (loading.current = true);
      recommendData[0].productList = [...data.paragraphs] as any;
      recommendData[0].localInfo = (data?.localInfo as any) || {};
      recommendData[0].userInfoVO = (data?.userInfoVO as any) || {};
      recommendStore.initData({
        sceneCode,
        subSceneCode,
        mainSkus,
        bulkPrice,
        c3Id,
        channelPage,
        activeTab: 0,
        recommendData,
      });
      loading.current = false;
    };

    const defaultRecommendData = () => {
      recommendStore.initData({
        sceneCode,
        subSceneCode,
        mainSkus,
        bulkPrice,
        c3Id,
        channelPage,
        activeTab: 0,
        recommendData: [],
      });
    };

    //处理推荐页面的点击埋点数据，根据不同页面引用不同的方法
    const initTrackParams = () => {
      switch (sceneCode) {
        case SceneCode.HOME_PAGE:
          productTrackParams.current = {
            ...productTrackParams.current,
            productClick: HOME_PAGE_sendClickTrack.Home_RecomSku,
            addCartClick: HOME_PAGE_sendClickTrack.Home_RecomSku_Addcart,
          };
          break;
        case SceneCode.HOME_CHANNEL_PAGE:
          productTrackParams.current = {
            ...productTrackParams.current,
            productClick: HOME_CHANNEL_PAGE_sendClickTrack.Home_Mini_Hp_RecomSku,
            addCartClick: HOME_CHANNEL_PAGE_sendClickTrack.Home_Mini_Hp_RecomSku_Addcart,
          };
          break;
        default:
          break;
      }
    };

    useEffect(() => {
      initTrackParams();
      recommendStore.setProp('minShowCount', minShowCount);
      return () => {
        recommendStore.clearData();
      };
    }, []);

    useEffect(() => {
      if (isVisible) {
        if (
          !isShowViewMore &&
          recommendStore.currentRecommendData &&
          !recommendStore.currentRecommendData.noMores &&
          !recommendStore.currentRecommendData.errors
        ) {
          recommendStore.currentRecommendData.loadProductData();
        }
      }
    }, [isVisible, recommendStore?.currentRecommendData?.productList?.length]);

    //为了处理个人中心多页面公用同一个推荐组件不刷新的问题,等个人中心重构时就可以删掉
    useEffect(() => {
      // 路由变化时的处理逻辑，
      //如果是客户端调用组件时，需要初始化store
      if (!initRecommendStore) {
        initRecommend();
      }
    }, [pathname]);

    useEffect(() => {
      if (recommendStore?.currentRecommendData?.productList?.length > 0) {
        changeViewMore(); //需要实时算出是否显示View more按钮，作为参数传入initObserver
      }
    }, [recommendStore?.activeTab, recommendStore?.currentRecommendData?.productList?.length]);

    // 如果没有tab数据，不显示组件
    if (recommendStore.recommendData.length === 0) {
      return null;
    }

    // 如果第一个tab下没有推荐商品数据，则不显示组件；其他tab下没有数据时依然显示组件
    if (
      recommendStore.activeTab === 0 &&
      recommendStore.currentRecommendData.productList.length === 0
    ) {
      return null;
    }

    if (minShowCount && recommendStore.currentRecommendData?.productList?.length <= minShowCount) {
      return null;
    }

    const isLoading =
      !recommendStore.currentRecommendData?.errors &&
      !recommendStore.currentRecommendData?.noMores &&
      recommendStore.currentRecommendData?.loading;

    return (
      <div className={isSearch ? style.searchRecommendWrapper : style.jdi_recommend_wrapper}>
        <div className={style.tab_headers}>
          <div className={style.tab_header_wrapper}>
            {recommendStore?.recommendData.map((tab: RecommendTab, index: number) => (
              <h2
                key={tab.slotId || index}
                className={`${style.tab_header} 
                  ${sceneCode === SceneCode.HOME_PAGE || sceneCode === SceneCode.HOME_CHANNEL_PAGE ? style.tab_header_home : ''}
                  ${index === recommendStore.activeTab ? style.active : ''}
                  ${recommendStore?.recommendData.length > 1 ? style.tab_header_more : ''}
                `}
                onClick={() => handleTabClick(tab)}
              >
                {tab.slotName}
              </h2>
            ))}
          </div>
        </div>
        <ProductList
          from='recommend'
          products={
            recommendStore &&
            recommendStore.currentRecommendData &&
            getSnapshot(recommendStore?.currentRecommendData?.productList)
          }
          localInfo={recommendStore?.currentRecommendData?.localInfo}
          productTrackParams={{
            ...productTrackParams.current,
            tab_name: recommendStore.currentRecommendData?.slotName,
          }}
          userInfo={recommendStore.currentRecommendData?.userInfoVO}
          addCartFn={addCartFn}
          onAddCartSuccess={onAddCartSuccess}
          shouldShowModal={shouldShowModal}
          shouldShowService={shouldShowService}
        />

        <div className={style.loadMore} ref={loadMoreRef}>
          {isShowViewMore &&
          !isLoading &&
          !recommendStore.currentRecommendData?.errors &&
          !recommendStore.currentRecommendData?.noMores ? (
            <div className={style.viewMore}>
              <button className={style.moreBtn} onClick={handleMore}>
                {t('productPool.viewMore')}
              </button>
            </div>
          ) : null}
        </div>
        {isLoading ? (
          <UKPageLoading className={style.loading} type='row' />
        ) : recommendStore.currentRecommendData?.errors ? (
          <UKPageError
            text={t('common.pageStatus.refresh')}
            clickTryAgain={() => recommendStore.currentRecommendData?.loadProductData()}
            btnText={t('common.btnText.refresh')}
            style={{ marginTop: '0px' }}
          />
        ) : recommendStore.currentRecommendData?.noMores ? (
          <div className={style.reached}>
            {t(
              `${
                recommendStore.currentRecommendData?.productList.length
                  ? 'recommend.noMore'
                  : 'recommend.nothingMore'
              }`,
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

export default Recommend;
